import { action, observable, store } from '~/common/mobx.decorator'
import { notifyStore } from '~/stores'
import { RECAPTCHA_CLIENT_KEY } from '~/common/helpers'
import {
	apolloClient,
	CHECK_REMAINING_CHANGE_PHONE_QUERY,
	CHECK_REMAINING_OTP_REQUESTS_QUERY,
	GET_REMAINING_OTP_REQUESTS_QUERY,
	SEND_OTP_CODE_MUTATION,
	VERIFY_OTP_CODE_MUTATION,
} from '~/common/apollo'
import { ERROR_CODE } from '~/common/constants'
import { logDefaultActionEvent } from '~/common/tracking/event-client.tracking'
import { ACTIATION_TRACKING_CONSTANT } from '~/features/care-activation/care-activation.constant'
import React from 'react'

const RESENT_TIME_IN_SECONDS = 60 * 1000
const RESENT_TIME_INTERVAL = 1000

@store()
class OtpDialogStore {
	@observable showDialogOtp = false
	@observable phoneNumber = ''
	@observable phoneInvalidConfirm = false
	@observable phoneInvalidMessage = ''
	@observable sendOtpCodeDone = false
	@observable remainingOtpSessionRetries
	@observable remainingOtpTotalRetries
	@observable initialErrorCode
	@observable initialErrorMessage
	@observable errorCode
	@observable errorMessage
	@observable submitErrorCode
	@observable submitErrorMessage
	@observable loadingInitial = false

	@observable resendTimeLeft = RESENT_TIME_IN_SECONDS
	@observable intervalId
	@observable timerRunning = false

	@observable phoneChangeRemaining = 0
	@observable remainingSessionResendCode = 0
	@observable remainingTotalResendCode = 0
	@observable checkRemainingChangePhone = null

	@observable checkRemainingOtpRequests = null

	@action
	startTimer = () => {
		this.intervalId = setInterval(() => {
			if (this.resendTimeLeft <= 0) {
				clearInterval(this.intervalId)
				this.timerRunning = false
				return
			}
			this.resendTimeLeft = this.resendTimeLeft - RESENT_TIME_INTERVAL
			this.timerRunning = true
		}, RESENT_TIME_INTERVAL)
	}

	@action
	resetTimer = () => {
		clearInterval(this.intervalId)
		this.timerRunning = false
		this.resendTimeLeft = RESENT_TIME_IN_SECONDS
	}

	@observable
	setPhoneNumber = (value) => {
		this.phoneNumber = value
	}

	@action
	setShowDialogOtp = (value) => {
		this.showDialogOtp = value
	}

	@action
	fetchInitialVerifyData = async (phoneNumber) => {
		this.loadingInitial = true
		const { data, errors } = await apolloClient.query({
			query: GET_REMAINING_OTP_REQUESTS_QUERY,
		})

		const errorCode = errors?.[0]?.extensions?.error

		if (errorCode) {
			this.initialErrorCode = errorCode
			this.initialErrorMessage = errors?.[0]?.message
			this.loadingInitial = false

			return
		}

		this.initialErrorCode = ''
		this.initialErrorMessage = ''

		this.remainingOtpTotalRetries = data?.checkRemainingOtpRequests?.remainingTotalResendCode
		this.remainingOtpSessionRetries = data?.checkRemainingOtpRequests?.remainingSessionResendCode

		void this.handleSendOTP({ phoneNumber }, {}).finally(() => {
			this.loadingInitial = false
		})
	}

	@action
	handleSendOTP = async (variables, { setErrors }) => {
		return new Promise((resolve, reject) => {
			window.grecaptcha.ready(async () => {
				try {
					const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_CLIENT_KEY, { action: 'sendOtpCode' })
					const { data } = await apolloClient.mutate({
						mutation: SEND_OTP_CODE_MUTATION,
						variables: {
							...variables,
							recaptchaToken,
						},
					})

					this.remainingOtpSessionRetries = data?.sendOtpCode?.remainingSessionResendCode
					this.remainingOtpTotalRetries = data?.sendOtpCode?.remainingTotalResendCode

					logDefaultActionEvent(ACTIATION_TRACKING_CONSTANT.PROFILE_COMPLETION_CONFIRM_VERIFY_PHONE_SUBMIT_SUCCESS, {
						phone_number: variables?.phoneNumber,
						remaining_session_resend_code: data?.sendOtpCode?.remainingSessionResendCode,
						remaining_total_resend_code: data?.sendOtpCode?.remainingTotalResendCode,
					})

					this.errorCode = ''
					this.errorMessage = ''

					this.submitErrorCode = ''
					this.submitErrorMessage = ''

					this.loadingInitial = false
				} catch (error) {
					const { graphQLErrors } = error
					this.errorCode = graphQLErrors?.[0]?.extensions?.error
					this.errorMessage = graphQLErrors?.[0]?.message

					logDefaultActionEvent(ACTIATION_TRACKING_CONSTANT.PROFILE_COMPLETION_CONFIRM_VERIFY_PHONE_SUBMIT_FAILED, {
						phone_number: variables?.phoneNumber,
						error_message: this.errorMessage,
					})

					if (!Object.values(ERROR_CODE).includes(this.errorCode)) {
						typeof setErrors === 'function' ? setErrors({ otpCode: this.errorMessage }) : void notifyStore.error(this.errorMessage)
						this.errorCode = ''
					}
					this.loadingInitial = false
				} finally {
					if (!this.timerRunning) {
						this.resetTimer()
						this.startTimer()
					}
				}
			})
		})
	}

	@action
	handleSubmitOTP = async (variables, { setErrors }, onOk) => {
		return new Promise((resolve, reject) => {
			window.grecaptcha.ready(async () => {
				try {
					const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_CLIENT_KEY, { action: 'verifyOtpCode' })

					const { data } = await apolloClient.mutate({
						mutation: VERIFY_OTP_CODE_MUTATION,
						variables: {
							...variables,
							recaptchaToken,
						},
					})

					if (data?.verifyOtpCode?.success) {
						notifyStore.success('$MESSAGES.SUCCESSFUL')
						this.submitErrorCode = ''
						this.submitErrorMessage = ''

						this.errorCode = ''
						this.errorMessage = ''

						logDefaultActionEvent(ACTIATION_TRACKING_CONSTANT.VERIFY_PHONE_SUBMIT_SUCCESS, {
							phone_number: variables?.phoneNumber,
						})

						typeof onOk === 'function' && onOk()
						resolve(true)
					}
				} catch (error) {
					const { graphQLErrors } = error
					this.submitErrorCode = graphQLErrors?.[0]?.extensions?.error
					this.submitErrorMessage = graphQLErrors?.[0]?.message

					logDefaultActionEvent(ACTIATION_TRACKING_CONSTANT.VERIFY_PHONE_SUBMIT_FAILED, {
						phone_number: variables?.phoneNumber,
						error_message: this.submitErrorMessage,
					})

					if (!Object.values(ERROR_CODE).includes(this.submitErrorCode)) {
						typeof setErrors === 'function' && setErrors({ otpCode: this.submitErrorMessage })
					} else {
						notifyStore.error(this.submitErrorMessage)
					}
				}
			})
		})
	}

	@action
	setPhoneInvalidConfirm = (value) => {
		this.phoneInvalidConfirm = value
	}

	@action
	fetchCheckRemainingChangePhone = async () => {
		try {
			const { data } = await apolloClient.query({
				query: CHECK_REMAINING_CHANGE_PHONE_QUERY,
			})
			this.checkRemainingChangePhone = data?.checkRemainingChangePhone
			return data?.checkRemainingChangePhone
		} catch (e) {
			await notifyStore.error(e.message)
		}
	}

	@action
	fetchCheckRemainingOtpRequests = async (phoneNumber) => {
		try {
			if (!!phoneNumber) {
				const { data, errors } = await apolloClient.query({
					query: CHECK_REMAINING_OTP_REQUESTS_QUERY,
					variables: {
						phoneNumber,
					},
				})
				const errorCode = errors?.[0]?.extensions?.error

				if (errorCode === ERROR_CODE.PHONE_EXISTED) {
					await notifyStore.open(
						<span
							dangerouslySetInnerHTML={{
								__html: `Oops! It looks like the phone number <strong style="color: #333335;">${phoneNumber
									?.replace('+1', '')
									.replace(
										/(\d{3})(\d{3})(\d{4})/,
										'($1) $2-$3'
									)}</strong> you entered is already associated with an existing account. Please use a different phone number to create your account or contact our support for assistance.`,
							}}
						/>,
						{
							cancelText: 'Cancel',
							title: 'Phone number already exists',
							showDialog: true,
							keepOriginal: true,
							okText: 'Update phone number',
							onOk: (key) => {
								notifyStore.closeDialog(key)
							},
							onCancel: (key) => {
								notifyStore.closeDialog(key)
							},
							showButtonClose: false,
						}
					)
				} else {
					this.checkRemainingOtpRequests = data?.checkRemainingOtpRequests
					return data?.checkRemainingOtpRequests
				}
			}
		} catch (e) {
			await notifyStore.error(e.message)
		}
	}
}

export const otpDialogStore = new OtpDialogStore()
